/**
 * WorkOrderCreatePage - Thin wrapper page for creating a new work order.
 *
 * Delegates all form logic to the shared JobForm organism configured with
 * formType="work_order". The page provides the layout chrome (back link,
 * title) and navigation on successful creation.
 *
 * Three pre-fill modes are supported via mutually-exclusive query params:
 *
 *   ?duplicate=<id>
 *     Standard duplicate — copies all fields from the source WO except
 *     scheduling/status/log fields, which the user fills in fresh. Produces
 *     an independent, regular work order (no parent linkage).
 *
 *   ?follow_up_from_wo=<id>
 *     Customer Service / Follow-Up spawned from a parent work order. Copies
 *     the customer and project location, locks the customer field, pre-ticks
 *     "Non Billable" in billing_info, and stamps parent_work_order_id on
 *     submit so the new record links back to the source.
 *
 *   ?follow_up_from_inspection=<id>
 *     Same as above but for follow-ups spawned from a failed inspection (one
 *     with >=1 item rated Needs Attention or Critical). Carries
 *     parent_inspection_id instead of parent_work_order_id.
 *
 * Also supports parent-project attachment via `?project_work_order_id=<id>`.
 * When present, the parent project is fetched and the new WO inherits its
 * company, building (location), and address, plus the whole Project
 * Information block the project was set up with — project name,
 * description, and business line — so none of it has to be retyped. Every
 * inherited value stays editable; it is a starting point, not a lock. The
 * page also renders a small allocation panel above the form so the user
 * can record how many hours from the project's pool this WO will draw from.
 *
 * On submit success, navigates to the newly created work order's detail page
 * (or back to the parent project when this WO was created as a child).
 *
 * @route /work-orders/create
 * @route /work-orders/create?duplicate=<id>
 * @route /work-orders/create?follow_up_from_wo=<id>
 * @route /work-orders/create?follow_up_from_inspection=<id>
 * @route /work-orders/create?project_work_order_id=<id>
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { JobForm } from '../../components/organisms/JobForm';
import { workOrderService } from '../../services/workOrderService';
import { inspectionService } from '../../services/inspectionService';
import { projectWorkOrderService } from '../../services/projectWorkOrderService';
import type { ProjectWorkOrder } from '../../services/projectWorkOrderService';
import { camelizeKeys } from '../../services/api';
import { BackLink } from '../../components/atoms';
import { ProjectHoursSummary } from '../../components/molecules';
import { PageContainer } from '../../components/templates/PageContainer';

/* ============================================
 * COMPONENT
 * ============================================ */

const WorkOrderCreatePage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	/** Source IDs — at most one of these is non-null. */
	const duplicateId = searchParams.get('duplicate');
	const followUpFromWoId = searchParams.get('follow_up_from_wo');
	const followUpFromInspectionId = searchParams.get('follow_up_from_inspection');

	/** Project Work Order this new WO should be attached to. Set when the
	 *  user clicks "Add Work Order" from a project's detail page. */
	const projectWorkOrderId = searchParams.get('project_work_order_id');

	/* Pre-populated initial data for the JobForm. Shape matches WorkOrder
	   field names; JobForm normalizes camelCase / snake_case as needed.
	   Populated from one of the source IDs above (duplicate / follow-up). */
	const [initialData, setInitialData] = useState<Record<string, any> | undefined>(undefined);

	/** Loading state while fetching the source record (WO or Inspection). */
	const [isPreFilling, setIsPreFilling] = useState(
		Boolean(duplicateId || followUpFromWoId || followUpFromInspectionId)
	);

	/** Parent project, fetched when projectWorkOrderId is set. Drives the
	 *  inheritance prefill (company / building / address) and the
	 *  allocation panel rendered above the form. */
	const [parentProject, setParentProject] = useState<ProjectWorkOrder | null>(null);
	const [loadingProject, setLoadingProject] = useState(Boolean(projectWorkOrderId));

	/** Allocation input — buffered as a string so the field can be empty
	 *  mid-typing. Submitted as a number. Defaults to the project's
	 *  remaining hours so the typical "fill the rest of the pool" flow
	 *  takes no typing. */
	const [allocatedHoursDraft, setAllocatedHoursDraft] = useState<string>('');

	/* Pre-fill strategy is mode-specific:
	 *  - duplicate: copy everything except date/time/status/logs
	 *  - follow-up from WO: copy customer + location + addresses; leave
	 *    project name/description blank for the user; tag Non Billable.
	 *  - follow-up from inspection: copy customer + location; tag Non
	 *    Billable; leave the rest blank. */
	useEffect(() => {
		const run = async () => {
			try {
				if (duplicateId) {
					const response = await workOrderService.getById(duplicateId);
					const source = (response.data as any)?.data ?? response.data;
					/* Strip date/time and identity fields — the duplicate
					   should be a fresh record with new scheduling. */
					const {
						id, createdAt, created_at, updatedAt, updated_at,
						scheduledDate, scheduled_date,
						startTime, start_time, endTime, end_time,
						status, completionDate, completion_date,
						timeLogs, time_logs, photos,
						parentWorkOrderId, parent_work_order_id,
						parentInspectionId, parent_inspection_id,
						...rest
					} = source;
					setInitialData(rest);
				} else if (followUpFromWoId) {
					const response = await workOrderService.getById(followUpFromWoId);
					const source = (response.data as any)?.data ?? response.data;
					setInitialData(buildFollowUpFromWorkOrder(source, followUpFromWoId));
				} else if (followUpFromInspectionId) {
					const response = await inspectionService.getById(followUpFromInspectionId);
					const source = (response.data as any)?.data ?? response.data;
					setInitialData(buildFollowUpFromInspection(source, followUpFromInspectionId));
				}
			} catch (err) {
				console.error('[WorkOrderCreatePage] Failed to pre-fill from source:', err);
			} finally {
				setIsPreFilling(false);
			}
		};
		run();
	}, [duplicateId, followUpFromWoId, followUpFromInspectionId]);

	/**
	 * Fetch the parent project when project_work_order_id is in the URL.
	 * The response carries the project's company_id, location summary,
	 * and the hour pool totals — enough to drive both the form prefill
	 * and the allocation panel below.
	 */
	useEffect(() => {
		if (!projectWorkOrderId) return;
		let cancelled = false;
		(async () => {
			try {
				const res = await projectWorkOrderService.getById(projectWorkOrderId);
				if (cancelled) return;
				const rawData = (res.data as any)?.data ?? res.data;
				const proj = camelizeKeys(rawData) as ProjectWorkOrder;
				setParentProject(proj);
				/* Default the estimate to the PROJECTED remaining hours — what is
				 * left after both the actual spend and the estimates already
				 * outstanding on other open work orders. Defaulting to the raw
				 * remaining balance would double-book hours that sibling work
				 * orders have already earmarked. */
				const projected = Number(proj.hoursProjectedRemaining ?? proj.hoursRemaining ?? 0);
				if (Number.isFinite(projected) && projected > 0) {
					setAllocatedHoursDraft(String(projected));
				}
			} catch (err) {
				console.error('[WorkOrderCreatePage] Failed to fetch parent project:', err);
			} finally {
				if (!cancelled) setLoadingProject(false);
			}
		})();
		return () => { cancelled = true; };
	}, [projectWorkOrderId]);

	/**
	 * Compose the initialData payload handed to JobForm. The source
	 * (duplicate / follow-up) data takes priority; parent-project
	 * inheritance fields layer in underneath so they only apply when no
	 * source data is present. The order matters — we want
	 * parent-project inheritance to win over no value but lose to an
	 * explicit source value.
	 */
	const composedInitialData = useMemo(() => {
		const base: Record<string, any> = {};
		if (parentProject) {
			base.company_id = parentProject.companyId;
			if (parentProject.locationId) base.location_id = parentProject.locationId;
			const loc = parentProject.location;
			if (loc) {
				/* JobForm tracks the street-line address as a single
				 * string. Compose it here so the field has meaningful
				 * content even before the user touches it. */
				base.project_address = [loc.address, loc.city, loc.state, loc.zip]
					.filter((v) => v && String(v).trim() !== '')
					.join(', ');
			}
			/* Project Information carries over from the project setup —
			 * the user already entered all of this once when standing the
			 * project up, so the child form opens pre-filled instead of
			 * blank. Each field remains editable: the project name is the
			 * expected starting point for a child WO's title (e.g. append
			 * "— Floor 2") and the description is the shared scope text.
			 * Only seed keys the project actually has, so a project with
			 * no description doesn't blank out a value from elsewhere. */
			base.project_name = parentProject.name ?? '';
			if (parentProject.description) base.description = parentProject.description;
			if (parentProject.businessLine) base.business_line = parentProject.businessLine;
		}
		if (initialData) {
			return { ...base, ...initialData };
		}
		/* Return undefined when there's nothing to seed so JobForm skips
		 * its initialData prefill pass entirely. */
		return Object.keys(base).length > 0 ? base : undefined;
	}, [parentProject, initialData]);

	const allocatedHoursNum = Number(allocatedHoursDraft);
	const projectAllocatedHours = Number.isFinite(allocatedHoursNum) && allocatedHoursNum >= 0
		? allocatedHoursNum
		: undefined;

	/** Whether the customer field should be locked (yes for follow-ups
	 *  AND project children, since the project's customer is authoritative). */
	const lockCustomer = Boolean(followUpFromWoId || followUpFromInspectionId || projectWorkOrderId);

	/** Page title — accounts for all four create variants. Duplicate >
	 *  follow-up > project > plain. */
	const pageTitle = duplicateId
		? 'Duplicate Work Order'
		: (followUpFromWoId || followUpFromInspectionId)
			? 'Create Follow-Up Work Order'
			: parentProject
				? `New Work Order — ${parentProject.name}`
				: 'New Work Order';

	const isLoading = isPreFilling || (Boolean(projectWorkOrderId) && loadingProject);

	return (
		<PageContainer block="work-order-create-page" dataRef="page-work-order-create-page">
			{/* --- Back navigation link ---
			    When this WO is a project child, points back at the
			    parent project so the user lands where they started;
			    otherwise back to the Work Orders list. */}
			<BackLink
				onClick={() => {
					if (projectWorkOrderId) navigate(`/project-work-orders/${projectWorkOrderId}`);
					else navigate('/work-orders');
				}}
				label={projectWorkOrderId ? 'Back to Project' : 'Back to Work Orders'}
			/>

			{/* --- Page header --- */}
			<h1 className="page-title text-24px font-bold font-heading text-brand-heading mb-8px">{pageTitle}</h1>

			{/* --- Project allocation panel — only when this WO is a child of
			    a project. Shows the inheritance + the hours pool context,
			    plus the field that records this WO's share of the pool. */}
			{parentProject && (
				<section className="bg-white border border-brand-border rounded-md p-16px tablet:p-20px mb-16px">
					<h2 className="text-16px font-semibold text-brand-text mb-12px">Project Assignment</h2>

					{/* Shared hours summary — the estimate entered below is
					    earmarked against the project, but only draws the pool
					    down once this work order is closed with actual hours. */}
					<ProjectHoursSummary
						className="mb-16px"
						size="sm"
						totalHours={Number(parentProject.totalHours ?? 0)}
						estimatedOpen={Number(parentProject.hoursEstimatedOpen ?? 0)}
						actualUsed={Number(parentProject.hoursActualUsed ?? 0)}
						remaining={Number(parentProject.hoursRemaining ?? 0)}
						projectedRemaining={Number(parentProject.hoursProjectedRemaining ?? 0)}
						variance={Number(parentProject.hoursVariance ?? 0)}
						hasClosedWork={Number(parentProject.hoursEstimatedClosed ?? 0) > 0 || Number(parentProject.hoursActualUsed ?? 0) > 0}
					/>

					{/* Inheritance summary — concise readout of what the new WO
					    picks up from the project. The building/address chain is
					    fixed by the project; the Project Information fields are
					    pre-filled but editable, so the copy says so explicitly
					    rather than implying they're locked. */}
					{parentProject.location && (
						<p className="text-12px text-brand-text-muted mb-4px">
							This work order inherits its building and address from the project:{' '}
							<span className="font-medium text-brand-text">{parentProject.location.name}</span>
							{parentProject.location.address ? ` — ${parentProject.location.address}, ${parentProject.location.city} ${parentProject.location.state}` : ''}
						</p>
					)}
					<p className="text-12px text-brand-text-muted mb-12px">
						Project Information (name, description, business line) is pre-filled from the project — edit it below if this work order needs its own.
					</p>

					{/* Allocation input — pre-filled with the project's
					    remaining hours so the common "use the rest" flow is
					    one click. The backend ultimately decides whether to
					    accept an over-allocation; we keep the input loose
					    so the user can experiment. */}
					<div className="max-w-[280px]">
						<label className="block text-12px font-medium text-brand-text mb-4px">
							Estimated Hours for this Work Order
						</label>
						<input
							type="number"
							min={0}
							step={0.25}
							value={allocatedHoursDraft}
							onChange={(e) => setAllocatedHoursDraft(e.target.value)}
							placeholder="0"
							className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-primary"
						/>
						<p className="mt-4px text-12px text-brand-text-muted">
							Budget estimate for this work order. It is earmarked against the project's pool now, and charged against it as actual hours when the work order is closed out.
						</p>
					</div>
				</section>
			)}

			{/* --- Follow-up context banner so the user always sees what
			    they're following up on. Plain-text; no link out to the
			    parent to keep the user in the create flow. --- */}
			{(followUpFromWoId || followUpFromInspectionId) && !isPreFilling && initialData && (
				<div className="mb-24px px-16px py-12px bg-brand-surface border border-brand-border rounded-md text-14px font-sans text-brand-text">
					<span className="font-semibold">Follow-up for </span>
					{followUpFromWoId
						? `Work Order #${followUpFromWoId}${initialData?.project_name ? ` — ${initialData.project_name}` : ''}`
						: `Inspection #${followUpFromInspectionId}`}
					<span className="ml-12px text-brand-text-muted text-12px">Non-billable · customer locked to parent</span>
				</div>
			)}

			{/* --- Show loading spinner while fetching source / project --- */}
			{isLoading ? (
				<div className="flex items-center gap-8px text-brand-text-muted text-14px py-32px">
					<svg className="animate-spin h-20px w-20px text-brand-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
					</svg>
					Loading…
				</div>
			) : (
				/* --- JobForm organism configured for work order creation. --- */
				<JobForm
					formType="work_order"
					onSuccess={(id) => {
						/* When this WO was created as a child of a project,
						   bounce back to the project detail page so the new
						   row appears in its allocation editor. Follow-ups
						   and plain creates land on the new WO detail. */
						if (projectWorkOrderId) navigate(`/project-work-orders/${projectWorkOrderId}`);
						else navigate(`/work-orders/${id}`);
					}}
					initialData={composedInitialData}
					projectWorkOrderId={projectWorkOrderId ?? undefined}
					projectAllocatedHours={projectAllocatedHours}
					lockCompany={lockCustomer}
				/>
			)}
		</PageContainer>
	);
};

/**
 * Build the JobForm initialData payload for a follow-up spawned from a parent
 * work order. Carries forward the customer, location, project addresses, and
 * contact, and stamps parent_work_order_id + auto-ticks "Non Billable".
 * Scheduling / status / logs are intentionally left blank.
 */
function buildFollowUpFromWorkOrder(source: Record<string, any>, parentId: string): Record<string, any> {
	const billingInfo = Array.isArray(source.billing_info ?? source.billingInfo)
		? [...(source.billing_info ?? source.billingInfo)]
		: [];
	if (!billingInfo.includes('Non Billable')) billingInfo.push('Non Billable');

	return {
		parent_work_order_id: Number(parentId),
		company_id: source.company_id ?? source.companyId,
		location_id: source.location_id ?? source.locationId,
		project_address: source.project_address ?? source.projectAddress,
		project_address_line_2: source.project_address_line_2 ?? source.projectAddressLine2,
		project_city: source.project_city ?? source.projectCity,
		project_state: source.project_state ?? source.projectState,
		project_zip: source.project_zip ?? source.projectZip,
		contact_name: source.contact_name ?? source.contactName,
		contact_phone: source.contact_phone ?? source.contactPhone,
		contact_email: source.contact_email ?? source.contactEmail,
		project_name: '',
		description: '',
		business_line: source.business_line ?? source.businessLine,
		billing_info: billingInfo,
	};
}

/**
 * Build the JobForm initialData payload for a follow-up spawned from a failed
 * inspection. Carries forward the customer and inspection location; everything
 * else is blank for the user to fill in. Auto-ticks "Non Billable".
 */
function buildFollowUpFromInspection(source: Record<string, any>, parentId: string): Record<string, any> {
	const billingInfo: string[] = ['Non Billable'];
	return {
		parent_inspection_id: Number(parentId),
		company_id: source.company_id ?? source.companyId,
		location_id: source.location_id ?? source.locationId,
		project_name: '',
		description: '',
		billing_info: billingInfo,
	};
}

export default WorkOrderCreatePage;
